import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostJson } from './client'

/**
 * Settings → Easeedesk → Form builder.
 *
 * Perechile vechi: `settings/easeedesk/ticket_form_builder`, `get_form_list`,
 * `create_form`, `clone_form`, `remove_form`, `get_ticket_edit_form`,
 * `get_sortable_field`, `update_ticket_form` - si `new_ticket_form_builder`,
 * `get_forms`, `edit_form_data`, acelasi ecran cu alt drept, care din 20
 * octombrie 2026 intra tot aici (fila lui s-a scos).
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

/** `standard` = din `it_easeedesk_field_list`, `custom` = camp propriu. */
export type FieldKind = 'standard' | 'custom'

export interface FormListEntry {
  ID: number
  form_name: string
  form_description: string
  fields: number
  custom_fields: number
  /** Perechile tip + status care folosesc formularul: `['Incident / New', ...]`. */
  used_by: string[]
}

export interface FormBuilderShell {
  title: string
  moduleUrl: string
  /** Fereastra mica de camp propriu din editor apare numai cu dreptul ei. */
  canCreateCustomFields: boolean
}

/** Un camp asezat pe formular. */
export interface ConfiguredField {
  config_id: number
  ID: number
  /** `field_12` sau `custom_12`, cheia din `data-field` al machetelor vechi. */
  key: string
  custom_field: boolean
  field_name: string
  field_label: string
  field_type: string
  field_unique_id: string
  required: boolean
  /** `field_required_level = 1`: obligatoriu pe orice formular, nu se poate scoate. */
  always: boolean
  /** 0 toti, 1 utilizatorul, 2 administratorul. */
  visibility: number
  order: number
  options: string[]
  default_values: string[]
}

/** Un camp din paleta din care se aleg. */
export interface PaletteField {
  ID: number
  key: string
  custom_field: boolean
  field_name: string
  field_label: string
  field_type: string
  field_unique_id: string
  always: boolean
  configured: boolean
}

export interface FormEditor {
  formDetails: { ID: number; form_name: string; form_description: string }
  configured_fields: ConfiguredField[]
  fields: PaletteField[]
  custom_fields: PaletteField[]
}

/** Ce se trimite inapoi la salvare: lista INTREAGA, in ordinea de pe ecran. */
export interface SavedField {
  ID: number
  custom_field: boolean
  required: boolean
  visibility: number
}

export function formBuilderShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_form_builder'],
    queryFn: () => apiGet<FormBuilderShell>('/settings/easeedesk/ticket_form_builder'),
  })
}

export function formListQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_form_builder', 'list'],
    queryFn: () => apiGet<{ form_list: FormListEntry[] }>('/settings/easeedesk/ticket_form_builder/list'),
  })
}

export function formEditorQuery(form: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_form_builder', 'editor', form],
    queryFn: () => apiGet<FormEditor>(`/settings/easeedesk/ticket_form_builder/${form}`),
    enabled: form > 0,
  })
}

/** `get_sortable_field`: campul bifat, ca sa fie asezat pe loc in lista. */
export function paletteField(field: number, kind: FieldKind) {
  return apiGet<{ field: PaletteField }>(`/settings/easeedesk/ticket_form_builder/field/${field}/${kind}`)
}

export function createForm(form_name: string, form_description: string) {
  return apiPost<{ ok?: boolean; form_id?: number; form_name?: string }>('/settings/easeedesk/ticket_form_builder', {
    form_name,
    form_description,
  })
}

/** Copia formularului, cu toate campurile lui. Numele gol => "X (copie)". */
export function duplicateForm(form: number, form_name = '') {
  return apiPost<{ ok?: boolean; form_id?: number; form_name?: string; fields?: number }>(
    `/settings/easeedesk/ticket_form_builder/${form}/duplicate`,
    { form_name },
  )
}

/**
 * Prima cerere raspunde cu `confirm` si cu cine foloseste formularul; a doua,
 * cu `confirmed`, sterge.
 */
export function removeForm(form: number, confirmed = false) {
  return apiPost<{ ok?: boolean; confirm?: boolean; used_by?: string[] }>(
    `/settings/easeedesk/ticket_form_builder/${form}/remove`,
    { confirmed: confirmed ? 1 : 0 },
  )
}

/** Numele si campurile asezate. Arborele pleaca JSON, nu ca `field[3][order]`. */
export function saveForm(form: number, form_name: string, fields: SavedField[]) {
  return apiPostJson<{ ok?: boolean }>(`/settings/easeedesk/ticket_form_builder/${form}`, { form_name, fields })
}
